/**
 * Reading the reporter's own text for something that identifies their record.
 *
 * A browser-side twin of bugcatch-api/src/settings/reference-extract.ts, and
 * deliberately only the half this side needs. The server does the extraction
 * that counts — what a payload claims it found is not evidence, and the value
 * an investigation eventually joins on is the one the API read out of the text
 * it stored. This copy exists to answer one question, locally and instantly:
 *
 *   does what this person has already typed contain an identifier, or do we
 *   have to interrupt them for one?
 *
 * Getting that wrong in the lenient direction costs an unnecessary question.
 * Getting it wrong in the strict direction means asking a reporter who has
 * already told us, in the sentence above the box, which is the fastest way to
 * make somebody close a bug reporter and not come back. So the matching rule
 * here is the same rule, run over the same patterns, which the API ships with
 * the field list precisely so that both sides can agree.
 *
 * The patterns arrive over the wire and are compiled here. That is a thing to
 * be deliberate about rather than nervous about: they are shapes, not secrets —
 * "a loan number looks like LN- followed by digits" is not a fact worth hiding
 * from somebody already looking at their own loan number — and the alternative
 * is a round trip per keystroke or a question for everybody.
 */

const MAX_SCAN_CHARS = 20000;
const MAX_VALUE_CHARS = 120;
const MAX_MATCHES = 200;

/**
 * Compile, or null.
 *
 * Null on anything that will not compile, which in a browser is a real case
 * rather than a defensive one: a regex feature one engine lacks throws here
 * rather than failing to match. A field that cannot be compiled is simply not
 * scanned, the reporter gets asked, and the server — which compiled it fine —
 * still reads the text. Degrading to the question is the whole design.
 */
function compile(source) {
  if (typeof source !== 'string' || !source.trim() || source.length > 400) return null;
  try {
    return new RegExp(source.trim(), 'gi');
  } catch {
    return null;
  }
}

const tidy = (value) => String(value).replace(/\s+/g, ' ').trim().slice(0, MAX_VALUE_CHARS);

/** Group 1 where the pattern has one, the whole match otherwise. */
function valueOf(match) {
  const raw = match.length > 1 && match[1] !== undefined ? match[1] : match[0];
  if (typeof raw !== 'string' || !raw.trim()) return null;
  // The span claimed is the whole match, never the capture: a labelled rule has
  // eaten the words in front of the value too, and leaving them unclaimed lets
  // a later field match them again.
  return { value: tidy(raw), start: match.index, end: match.index + match[0].length };
}

/**
 * Every configured identifier found in the given text.
 *
 * Fields are taken in the order the project arranged them and a span an earlier
 * field claimed is not offered to a later one — which is what keeps the rule for
 * "alternate number" and the rule for "mobile number", which match the same ten
 * digits, from both claiming it.
 */
export function matchReferences(fields, texts) {
  const found = [];
  const seen = new Set();

  for (const block of texts || []) {
    const text = typeof block === 'string' ? block.slice(0, MAX_SCAN_CHARS) : '';
    if (!text) continue;

    const claimed = [];
    const overlaps = (a, b) => claimed.some(([s, e]) => a < e && b > s);

    for (const field of fields || []) {
      if (!field || field.scan === false) continue;
      const re = compile(field.pattern);
      if (!re) continue;

      let match;
      let guard = 0;
      re.lastIndex = 0;
      while ((match = re.exec(text)) !== null) {
        if (++guard > MAX_MATCHES) break;
        // exec does not advance past a zero-width match, so this would spin.
        if (match[0] === '') { re.lastIndex += 1; continue; }

        const hit = valueOf(match);
        if (hit && !overlaps(hit.start, hit.end)) {
          claimed.push([hit.start, hit.end]);
          const dedupe = `${field.key}:${hit.value.toLowerCase()}`;
          if (!seen.has(dedupe)) {
            seen.add(dedupe);
            found.push({ key: field.key, label: field.label, value: hit.value });
          }
        }
      }
    }
  }

  return found;
}

/**
 * Whether what somebody typed into the box is the shape that field asks for.
 *
 * Anchored, unlike the scan above: reading prose means finding a value inside a
 * sentence, and checking an answer means the answer *is* the value. `valuePattern`
 * where the field has one — a rule that finds "alternate number: 98…" in prose
 * needs the words, and a box already headed "Alternate number" will not have
 * them typed into it again.
 *
 * Returns the tidied value, or null. Advisory only: the widget uses it to say
 * "that does not look like a loan number" before sending, and the API keeps a
 * value that fails it rather than dropping what the reporter believes is their
 * reference.
 */
export function validateReference(field, raw) {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (!value || value.length > MAX_VALUE_CHARS) return null;

  const re = compile(field && (field.valuePattern || field.pattern));
  if (!re) return null;

  let match;
  let guard = 0;
  re.lastIndex = 0;
  while ((match = re.exec(value)) !== null) {
    if (++guard > MAX_MATCHES) break;
    if (match[0] === '') { re.lastIndex += 1; continue; }
    // One character of slack at the front and only there, for a pattern whose
    // left boundary consumes rather than looks behind — see the phone rule,
    // written that way so it compiles in older Safari.
    const toEnd = match.index + match[0].length === value.length;
    const atEdge = match.index === 0 || match.index === 1;
    if (toEnd && atEdge) {
      const hit = valueOf(match);
      if (hit) return hit.value;
    }
  }
  return null;
}

/**
 * What this project needs in order to look a report up.
 *
 * Every failure answers `{ policy: 'off', fields: [] }`, which is the answer
 * that draws nothing. A widget that could not reach this route must not be a
 * widget that refuses to send a report — the whole feature is a help, and the
 * server reads the text either way.
 */
export async function referenceFields({ apiBase, apiKey, projectId }) {
  const off = { policy: 'off', fields: [] };
  try {
    const res = await fetch(
      `${apiBase}/ingest/reference-fields?projectId=${encodeURIComponent(projectId)}`,
      { headers: { 'X-BugCatch-Key': apiKey } },
    );
    if (!res.ok) return off;
    const body = await res.json();
    const policy = body.policy === 'optional' || body.policy === 'required' ? body.policy : 'off';
    const fields = Array.isArray(body.fields) ? body.fields : [];
    return fields.length ? { policy, fields } : off;
  } catch {
    return off;
  }
}
